// The canonical Stage 4 pricing ledger, the reviewable manual price authority
// and the generated-amount floor. Moved here from
// src/utils/quote-pricing-readiness.ts (Quote Brain slice PC), which keeps the
// readiness check built on it.
//
// IMPORTANT PRICING AUTHORITY BOUNDARY
// ------------------------------------
// A final Stage 4 costBreakdown can contain three different kinds of dollars:
//   1. `finalIncGstAddon` / legacy `rateIsFinalIncGst` lines are a FINAL
//      customer charge-out price already including GST.
//   2. `finalExGstAddon` / `rateTaxBasis:'ex_gst'` lines are a FINAL customer
//      charge-out price before GST (Francis's authoritative Sell basis).
//   3. `totalCost` is the unmatched estimated cost/markup base (ex GST).
//
// The release floor therefore comes from one canonical FINAL Stage 4 scope
// ledger: rate-book charge-out ex GST + unmatched cost base ex GST. A raw Work
// Method `totalCost` is retained below as diagnostic/back-compat data only. It
// is an earlier projection and may mix charge-out with cost, so it must never
// override the final scope ledger or produce a false loss-making block.

import {
    hasExplicitPriceWorkingDriver,
    priceWorkingBillableQuantity,
} from './price-working-quantity.js';
import { partitionCostLines } from './cost-line-partition.js';
import { pricingRowHasPendingResolution } from './pricing-resolution.js';

type UnknownRecord = Record<string, any>;

export type ActualCostCoverage = 'none' | 'unknown' | 'partial' | 'complete';

export interface Stage4PricingLedgerItem {
    itemId: string;
    title: string;
    revenueExGst: number;
    rateBookChargeOutIncGst: number;
    rateBookChargeOutExGst: number;
    unmatchedCostBaseExGst: number;
    pricingFloorExGst: number;
    shortfall: number;
    /** Cent-honest: any shortfall over one cent. Display only — never gates. */
    belowFloor: boolean;
    /** Clears BOTH materiality legs (> $50 AND > 1% of floor). Drives warnings. */
    materialBelowFloor: boolean;
    actualBuilderCostExGst: number | null;
    actualCostCoverage: ActualCostCoverage;
}

/** Materiality legs for below-floor findings (operator decision, 2026-07-29). */
export const MATERIAL_SHORTFALL_ABS = 50;
export const MATERIAL_SHORTFALL_PCT = 0.01;

export interface Stage4PricingLedger {
    items: Stage4PricingLedgerItem[];
    quoteRevenueExGst: number;
    rateBookChargeOutIncGst: number;
    rateBookChargeOutExGst: number;
    unmatchedCostBaseExGst: number;
    pricingFloorExGst: number;
    pricingHeadroom: number;
    shortfall: number;
    underpricedItemCount: number;
    materialUnderpricedItemCount: number;
    actualBuilderCostExGst: number | null;
    actualMarginAmount: number | null;
    actualMarginPercent: number | null;
    actualCostCoverage: ActualCostCoverage;
}

function money(value: unknown): number {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    if (typeof value !== 'string') return 0;
    const parsed = Number(value.replace(/[$,\s]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
}

function nullableMoney(value: unknown): number | null {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value !== 'string' || value.trim() === '') return null;
    const parsed = Number(value.replace(/[$,\s]/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
}

export function roundCents(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function itemAmount(item: UnknownRecord): number {
    return money(item?.amount ?? item?.total ?? item?.price);
}

function breakdownFor(owner: UnknownRecord): UnknownRecord {
    const breakdown = owner?.costBreakdown || owner?.cost_breakdown || {};
    return breakdown && typeof breakdown === 'object' ? breakdown : {};
}

export function costBreakdownTotal(owner: UnknownRecord): number {
    const breakdown = breakdownFor(owner);
    const explicit = nullableMoney(breakdown?.totalCost ?? breakdown?.total_cost);
    // Zero is authoritative for a pure rate-book item: WMS recompute leaves
    // directCostTotal as the visible line sum but stamps totalCost=0 because
    // there is no unmatched builder-cost base. Falling through here would
    // count the final charge-out a second time.
    if (explicit != null && explicit >= 0) return explicit;

    const direct = money(breakdown?.directCostTotal ?? breakdown?.direct_cost_total);
    const contingency = money(breakdown?.contingencyAmount ?? breakdown?.contingency_amount);
    const overhead = money(breakdown?.overheadAmount ?? breakdown?.overhead_amount);
    return Math.max(0, direct + contingency + overhead);
}

function hasOwn(owner: UnknownRecord, key: string): boolean {
    return Object.prototype.hasOwnProperty.call(owner, key);
}

function explicitFinalAddons(breakdown: UnknownRecord): {
    incPresent: boolean;
    incAmount: number;
    exPresent: boolean;
    exAmount: number;
} {
    const incKey = hasOwn(breakdown, 'finalIncGstAddon')
        ? 'finalIncGstAddon'
        : (hasOwn(breakdown, 'final_inc_gst_addon') ? 'final_inc_gst_addon' : null);
    const exKey = hasOwn(breakdown, 'finalExGstAddon')
        ? 'finalExGstAddon'
        : (hasOwn(breakdown, 'final_ex_gst_addon') ? 'final_ex_gst_addon' : null);
    return {
        incPresent: incKey != null,
        incAmount: incKey == null ? 0 : Math.max(0, money(breakdown[incKey])),
        exPresent: exKey != null,
        exAmount: exKey == null ? 0 : Math.max(0, money(breakdown[exKey])),
    };
}

const COST_LINE_KEYS = ['materials', 'labour', 'equipment', 'subcontractors'] as const;

function costLines(breakdown: UnknownRecord): Array<{ key: string; line: UnknownRecord }> {
    const result: Array<{ key: string; line: UnknownRecord }> = [];
    for (const key of COST_LINE_KEYS) {
        const lines = Array.isArray(breakdown?.[key]) ? breakdown[key] : [];
        for (const line of lines) {
            if (line && typeof line === 'object') result.push({ key, line });
        }
    }
    const otherKey = Array.isArray(breakdown?.otherCosts)
        ? 'otherCosts'
        : (Array.isArray(breakdown?.other) ? 'other' : null);
    if (otherKey) {
        for (const line of breakdown[otherKey]) {
            if (line && typeof line === 'object') result.push({ key: otherKey, line });
        }
    }
    return result;
}

function priceWorkingCategory(key: string): string {
    return key === 'other' ? 'otherCosts' : key;
}

function lineQuantity(key: string, line: UnknownRecord): number {
    return roundCents(priceWorkingBillableQuantity(line, priceWorkingCategory(key)));
}

function hasValidLineQuantity(key: string, line: UnknownRecord, quantity: number): boolean {
    if (!Number.isFinite(quantity) || quantity < 0) return false;
    return quantity > 0 || hasExplicitPriceWorkingDriver(line, priceWorkingCategory(key));
}

function isKnownZeroBusinessCost(line: UnknownRecord, costRate: number): boolean {
    if (costRate !== 0) return true;
    const source = String(line?.costRateSource ?? line?.cost_rate_source ?? '').trim().toLowerCase();
    return source === 'project_manual' || source === 'builder_confirmed_zero';
}

function lineText(value: unknown): string {
    return String(value ?? '').trim().toLowerCase();
}

/**
 * A builder's own cost decision on a line: a manual override, a manual cost estimate or a project
 * cost they typed. The same authority Internal Pricing writes when a cost is entered by hand
 * (applyInternalPricingLedgerEdits) and the ledger reads back (hasManualCommercialAuthority).
 */
function hasManualCostAuthority(line: UnknownRecord): boolean {
    return line?.manualOverride === true
        || ['manual_override', 'manual_cost_estimate'].includes(lineText(line?.pricingStatus ?? line?.pricing_status))
        || ['project_manual', 'manual', 'manual_override', 'project_cost_estimate']
            .includes(lineText(line?._rateSource ?? line?._rate_source));
}

/**
 * A modelled (not final charge-out) cost line whose total is zero without both an authority and a
 * quantity behind that zero. A modelled line's builder cost IS its stated total, so a zero is a real
 * cost of nothing only when somebody said so AND there is a quantity to price. A typed zero rate is a
 * decision about the rate, not evidence of the quantity: with no quantity the total is zero whatever
 * the rate is. Anything else is a quantity or rate nobody has supplied: an unknown cost, whatever
 * the customer is charged. Pending-resolution lines are handled by the caller; final charge-out lines
 * need a recorded cost rate and a quantity driver and are judged by rateBookActualCost.
 */
function isUnconfirmedZeroModelledCost(key: string, line: UnknownRecord): boolean {
    if (line?.rateIsFinalChargeOut === true || line?.rateIsFinalIncGst === true
        || line?.rate_is_final_charge_out === true || line?.rate_is_final_inc_gst === true) return false;
    if (pricingRowHasPendingResolution(line)) return false;
    if (money(line?.total) !== 0) return false;
    const authorised = hasManualCostAuthority(line)
        || ['project_manual', 'builder_confirmed_zero'].includes(lineText(line?.costRateSource ?? line?.cost_rate_source));
    return !(authorised && lineQuantity(key, line) > 0);
}

function breakdownDeclaresZeroCost(breakdown: UnknownRecord): boolean {
    const values = ['directCostTotal', 'direct_cost_total', 'totalCost', 'total_cost']
        .filter(key => Object.prototype.hasOwnProperty.call(breakdown, key))
        .map(key => breakdown[key]);
    return values.length > 0 && values.every((value) => {
        if (value == null || String(value).trim() === '') return false;
        return Number.isFinite(Number(value)) && Number(value) === 0;
    });
}

/**
 * A managed statutory package whose persisted breakdown declares zero direct and project cost, with
 * no line awaiting resolution. Its producers stamp totalCost 0 and marginAmount 0 beside final
 * charge-out lines: the package passes through at cost (buildLedgerItem), so it carries no margin
 * and no recorded cost of its own. Job costing's line ledger (internal-pricing-ledger.js) reads the
 * same rule.
 */
export function isManagedZeroCostReceipt(item: UnknownRecord): boolean {
    const managedSystem = String(item?.systemManaged || item?.system_managed || '');
    if (managedSystem !== 'francis-compliance-v1' && managedSystem !== 'francis-hbcf-v1') return false;
    const breakdown = breakdownFor(item);
    return breakdownDeclaresZeroCost(breakdown)
        && !costLines(breakdown).some(({ line }) => pricingRowHasPendingResolution(line));
}

/**
 * Resolve actual cost-to-business coverage for the rate-book stream.
 *
 * Rate-book `total` is customer charge-out. An explicit non-negative
 * `costRate` turns a final line with a valid quantity driver into known builder
 * cost. The pricing worktable stores both rates in the same inc-GST basis, so
 * a complete rate-book cost is converted back to ex GST here. Missing or blank
 * rows/rates intentionally make margin unknown.
 */
function rateBookActualCost(
    breakdown: UnknownRecord,
    rawRateBookChargeOutExGst: number,
    gstExempt: boolean,
): { actualCostExGst: number | null; coverage: ActualCostCoverage } {
    if (rawRateBookChargeOutExGst <= 0.01) {
        return { actualCostExGst: 0, coverage: 'complete' };
    }

    const finalLines = costLines(breakdown).filter(({ line }) => (
        (line?.rateIsFinalChargeOut === true || line?.rateIsFinalIncGst === true)
        && money(line?.total) > 0
    ));
    if (finalLines.length === 0) {
        return { actualCostExGst: null, coverage: 'unknown' };
    }

    let representedChargeOutExGst = 0;
    let knownCostExGst = 0;
    let knownCount = 0;
    for (const { key, line } of finalLines) {
        const chargeOutBasis = String(line?.rateTaxBasis ?? line?.rate_tax_basis ?? '').toLowerCase();
        const lineChargeOut = money(line.total);
        representedChargeOutExGst += gstExempt || chargeOutBasis === 'ex_gst'
            ? lineChargeOut
            : lineChargeOut / 1.1;
        const costRate = nullableMoney(line?.costRate ?? line?.cost_rate);
        const quantity = lineQuantity(key, line);
        if (costRate == null
            || costRate < 0
            || !isKnownZeroBusinessCost(line, costRate)
            || !hasValidLineQuantity(key, line, quantity)) continue;
        const costBasis = String(line?.costRateTaxBasis ?? line?.cost_rate_tax_basis ?? '').toLowerCase();
        const lineCost = costRate * quantity;
        knownCostExGst += gstExempt || costBasis === 'ex_gst'
            ? lineCost
            : lineCost / 1.1;
        knownCount += 1;
    }

    const allRowsRepresented = Math.abs(representedChargeOutExGst - rawRateBookChargeOutExGst) <= 0.05;
    if (knownCount !== finalLines.length || !allRowsRepresented) {
        return {
            actualCostExGst: null,
            coverage: knownCount > 0 ? 'partial' : 'unknown',
        };
    }

    return {
        actualCostExGst: roundCents(knownCostExGst),
        coverage: 'complete',
    };
}

/** A saved methodology stamp, read with the same precedence and snake_case fallbacks everywhere it is used. */
export interface MethodologyBridgeStamps {
    methodologyId: string;
    /** The rate-card sell the methodology banded, ex GST (0 when never stamped). */
    bandedSellExGst: number;
    bandedSellPresent: boolean;
    /** Banded administration share, falling back to the unbanded share, ex GST. */
    administrationShareExGst: number;
    /**
     * The producer's whole bridge from the unrounded figure to the committed amount, inc GST. It INCLUDES the
     * minimum-attendance allocation below: that is a labelled part of this number, never an addition to it.
     */
    roundingDeltaIncGst: number;
    /** The minimum-attendance top-up inside `roundingDeltaIncGst`, inc GST (0 when not stamped). */
    minimumAttendanceIncGst: number;
    /** A stamp is present but is not a number, or is a negative banded sell, administration share or attendance. */
    invalid: boolean;
}

const stampPresent = (value: unknown): boolean => value !== undefined && value !== null
    && !(typeof value === 'string' && value.trim() === '');

/**
 * The single reader of the saved pricing-methodology stamps. buildLedgerItem (the canonical Stage 4 ledger) and the
 * Internal Pricing ledger both call it, so the two cannot disagree about which field wins or how it is parsed.
 */
export function readMethodologyBridgeStamps(breakdown: UnknownRecord): MethodologyBridgeStamps {
    const rawBanded = breakdown?.rateCardBandedSellExGst ?? breakdown?.rate_card_banded_sell_ex_gst;
    const rawAdmin = breakdown?.administrationShareExGst ?? breakdown?.administration_share_ex_gst;
    const rawBandedAdmin = breakdown?.bandedAdministrationShareExGst ?? breakdown?.banded_administration_share_ex_gst;
    const rawRounding = breakdown?.pricingRoundingDeltaIncGst ?? breakdown?.pricing_rounding_delta_inc_gst;
    const rawAttendance = breakdown?.minimumAttendanceAllocationIncGst
        ?? breakdown?.minimum_attendance_allocation_inc_gst;
    const notAnAmount = (raw: unknown, mayBeNegative: boolean): boolean => {
        if (!stampPresent(raw)) return false;
        const parsed = nullableMoney(raw);
        return parsed == null || (!mayBeNegative && parsed < 0);
    };
    return {
        methodologyId: String(breakdown?.pricingMethodologyId || breakdown?.pricing_methodology_id || '').trim(),
        bandedSellExGst: Math.max(0, money(rawBanded)),
        bandedSellPresent: nullableMoney(rawBanded) != null,
        administrationShareExGst: Math.max(0, money(rawBandedAdmin)) || Math.max(0, money(rawAdmin)),
        roundingDeltaIncGst: money(rawRounding),
        minimumAttendanceIncGst: Math.max(0, money(rawAttendance)),
        invalid: notAnAmount(rawBanded, false)
            || notAnAmount(rawAdmin, false)
            || notAnAmount(rawBandedAdmin, false)
            || notAnAmount(rawRounding, true)
            || notAnAmount(rawAttendance, false),
    };
}

function buildLedgerItem(item: UnknownRecord, index: number, isGstInclusive: boolean): Stage4PricingLedgerItem {
    const breakdown = breakdownFor(item);
    const partition = partitionCostLines(breakdown);
    const explicitAddons = explicitFinalAddons(breakdown);
    // Each tax-basis aggregate is independently authoritative when stamped.
    // WMS intentionally stamps both fields, including zero, so treating the
    // old inc-GST field as authority for the ex-GST stream loses Francis Sell.
    const rawFinalIncGst = roundCents(explicitAddons.incPresent
        ? explicitAddons.incAmount
        : partition.finalIncGstTotal);
    const rawFinalExGst = roundCents(explicitAddons.exPresent
        ? explicitAddons.exAmount
        : partition.finalExGstTotal);
    const gstExempt = item?.gstExempt === true || item?.gst_exempt === true;
    const gstFactor = gstExempt ? 1 : 1.1;
    const managedSystem = String(item?.systemManaged || item?.system_managed || '');
    const isManagedStatutoryPackage = managedSystem === 'francis-compliance-v1'
        || managedSystem === 'francis-hbcf-v1';
    const {
        methodologyId,
        bandedSellExGst,
        administrationShareExGst,
        roundingDeltaIncGst,
    } = readMethodologyBridgeStamps(breakdown);
    // `pricingRoundingDeltaIncGst` is the producer's complete bridge from
    // pricingUnroundedIncGst to the committed amount. Once minimum attendance
    // is applied it therefore contains BOTH the attendance allocation and the
    // residual nearest-$10 rounding (amount - unrounded). The separately
    // persisted minimum field is explanatory, not another charge. Adding both
    // here raised a valid $1,200 minimum-attendance item to $2,050 on reopen.
    const methodologyExGst = methodologyId
        ? bandedSellExGst
            + administrationShareExGst
            + roundingDeltaIncGst / gstFactor
        : rawFinalExGst;
    // The managed compliance row is already a fully assembled final package.
    // Older Stage 4 passes could restamp its components as `totalCost` and add
    // a project-admin share; neither is a second client charge. Its explicit
    // final add-ons therefore remain the sole ledger authority.
    const rateBookChargeOutExGst = isManagedStatutoryPackage
        ? roundCents(rawFinalExGst + (gstExempt ? rawFinalIncGst : rawFinalIncGst / 1.1))
        : roundCents(
            Math.max(0, methodologyExGst) + (gstExempt ? rawFinalIncGst : rawFinalIncGst / 1.1),
        );
    const rateBookChargeOutIncGst = isManagedStatutoryPackage
        ? roundCents(rawFinalExGst * gstFactor + rawFinalIncGst)
        : roundCents(Math.max(0, methodologyExGst) * gstFactor + rawFinalIncGst);

    let unmatchedCostBaseExGst: number;
    if (isManagedStatutoryPackage) {
        unmatchedCostBaseExGst = 0;
    } else if (explicitAddons.incPresent || explicitAddons.exPresent) {
        // WMS recompute stamps totalCost as the unmatched markup base when it
        // stamps finalIncGstAddon. Do not add the final charge-out twice.
        unmatchedCostBaseExGst = costBreakdownTotal(item);
    } else if (partition.hasFinalLines) {
        // Legacy final-line rows may have a mixed totalCost. Rebuild only the
        // unmatched base from its line partition plus explicit allocations.
        unmatchedCostBaseExGst = roundCents(
            Math.max(0, partition.markupDirectTotal)
            + Math.max(0, money(breakdown?.contingencyAmount ?? breakdown?.contingency_amount))
            + Math.max(0, money(breakdown?.overheadAmount ?? breakdown?.overhead_amount)),
        );
    } else {
        unmatchedCostBaseExGst = costBreakdownTotal(item);
    }
    unmatchedCostBaseExGst = roundCents(Math.max(0, unmatchedCostBaseExGst));

    const displayAmount = Math.max(0, itemAmount(item));
    const revenueExGst = roundCents(
        isGstInclusive && !gstExempt ? displayAmount / 1.1 : displayAmount,
    );
    const pricingFloorExGst = roundCents(rateBookChargeOutExGst + unmatchedCostBaseExGst);
    const shortfall = roundCents(Math.max(0, pricingFloorExGst - revenueExGst));
    const belowFloor = shortfall > 0.01;
    // OPERATOR DECISION (Peter 2026-07-29, docs/operator-decisions/2026-07-29-brief-pack.md #2):
    // a shortfall is MATERIAL only when it clears BOTH legs — more than $50 AND
    // more than 1% of the item's pricing floor. The AND-form is the only shape
    // that suppresses the measured noise (a 0.4% miss fails the % leg; a
    // cents-level miss fails the $ leg) without opening a hole at either end of
    // job size. `belowFloor` stays cent-honest for per-item DISPLAY; only
    // material findings drive warnings, and nothing hard-blocks on price.
    const materialBelowFloor = belowFloor
        && shortfall > MATERIAL_SHORTFALL_ABS
        && pricingFloorExGst > 0
        && shortfall > roundCents(pricingFloorExGst * MATERIAL_SHORTFALL_PCT);
    const rateBookCost = rateBookActualCost(
        breakdown,
        roundCents(rawFinalExGst + (gstExempt ? rawFinalIncGst : rawFinalIncGst / 1.1)),
        gstExempt,
    );
    // Cost is unknown, never a reliable zero, while a line awaits resolution OR a modelled line has
    // a zero nobody confirmed or no quantity behind it. rateBookActualCost answers "complete"
    // whenever there is no rate-book stream, so without this the unmatched cost base read as the
    // whole actual cost and the margin looked certain (6.0.93 live review: 95.0% with the material
    // lines unpriced). Customer sell and the cost base itself are unchanged. This does not depend on
    // what the customer is charged: a free or included item inside a quote with a charged one can
    // still cost the builder something. A system-managed package keeps its own receipt rule below.
    const hasUnknownCost = costLines(breakdown)
        .some(({ line }) => pricingRowHasPendingResolution(line))
        || (managedSystem === ''
            && costLines(breakdown).some(({ key, line }) => isUnconfirmedZeroModelledCost(key, line)));
    // A managed statutory package is immutable in Internal Pricing. When its
    // persisted breakdown explicitly declares zero direct and project cost,
    // that receipt settles its cost: it passes through at cost, so its builder
    // cost is what the client pays for it and it adds no margin. Reading the
    // zero as the builder's cost showed the whole package as profit (100% on
    // an HBCF-only quote, RBB/GPSC 2026-11-04). Missing cost fields remain
    // unknown and non-zero legacy totals remain ignored as before.
    const managedZeroCostReceipt = isManagedZeroCostReceipt(item);
    const actualCostCoverage = hasUnknownCost
        ? 'unknown'
        : managedZeroCostReceipt
            ? 'complete'
            : rateBookCost.coverage;
    const actualBuilderCostExGst = hasUnknownCost
        ? null
        : managedZeroCostReceipt
            ? revenueExGst
            : rateBookCost.coverage === 'complete'
                ? roundCents(unmatchedCostBaseExGst + (rateBookCost.actualCostExGst || 0))
                : null;

    return {
        itemId: String(item?.id ?? item?.code ?? index + 1),
        title: String(item?.title || ''),
        revenueExGst,
        rateBookChargeOutIncGst,
        rateBookChargeOutExGst,
        unmatchedCostBaseExGst,
        pricingFloorExGst,
        shortfall,
        belowFloor,
        materialBelowFloor,
        actualBuilderCostExGst,
        actualCostCoverage: hasUnknownCost
            ? 'unknown'
            : pricingFloorExGst <= 0.01
                ? 'none'
                : actualCostCoverage,
    };
}

/**
 * Build the canonical FINAL Stage 4 pricing ledger used by every release gate.
 * Ordinary inclusions are descriptive children of their priced parent and are
 * deliberately not treated as independent price lines here.
 */
export function buildStage4PricingLedger(
    scope: unknown = [],
    isGstInclusive = true,
): Stage4PricingLedger {
    const scopeItems = (Array.isArray(scope) ? scope.filter(Boolean) : []) as UnknownRecord[];
    const items = scopeItems
        .map((item, index) => buildLedgerItem(item as UnknownRecord, index, isGstInclusive));
    // Preserve QuoteMaker's established project-total GST rule: add the
    // customer-visible amounts first, then derive ex GST once. Per-parent
    // ledger rows still round independently for their safety comparison.
    const quoteRevenueExGst = roundCents(scopeItems.reduce((sum, item) => {
        const gstExempt = item?.gstExempt === true || item?.gst_exempt === true;
        const amount = Math.max(0, itemAmount(item));
        return sum + (isGstInclusive && !gstExempt ? amount / 1.1 : amount);
    }, 0));
    const rateBookChargeOutIncGst = roundCents(items.reduce((sum, item) => sum + item.rateBookChargeOutIncGst, 0));
    const rateBookChargeOutExGst = roundCents(items.reduce((sum, item, index) => {
        const sourceItem = scopeItems[index];
        const gstExempt = sourceItem?.gstExempt === true || sourceItem?.gst_exempt === true;
        return sum + (gstExempt ? item.rateBookChargeOutIncGst : item.rateBookChargeOutIncGst / 1.1);
    }, 0));
    const unmatchedCostBaseExGst = roundCents(items.reduce((sum, item) => sum + item.unmatchedCostBaseExGst, 0));
    const pricingFloorExGst = roundCents(rateBookChargeOutExGst + unmatchedCostBaseExGst);
    const pricingHeadroom = roundCents(quoteRevenueExGst - pricingFloorExGst);
    const shortfall = roundCents(items.reduce((sum, item) => sum + item.shortfall, 0));
    const underpricedItemCount = items.filter(item => item.belowFloor).length;
    const materialUnderpricedItemCount = items.filter(item => item.materialBelowFloor).length;

    // Cost coverage is judged on the items that charge the client or carry cost. An item that
    // charges with no cost data has no recorded cost, never a zero one: leaving it out measured
    // the whole quote's revenue against the costed items alone, so a quote priced from its own
    // lines read a 100% margin from its statutory package (RBB/TCWPH, 2026-11-04). A managed
    // zero-cost receipt passes through at cost: it neither blocks a margin nor records one.
    const receipts = scopeItems.map(isManagedZeroCostReceipt);
    const judgedItems = items.filter((item, index) => !receipts[index]
        && (item.pricingFloorExGst > 0.01
            || item.revenueExGst > 0.01
            || item.actualCostCoverage === 'unknown'
            || item.actualCostCoverage === 'partial'));
    const coverageCount = (coverage: ActualCostCoverage) => judgedItems
        .filter(item => item.actualCostCoverage === coverage).length;
    const actualCostCoverage: ActualCostCoverage = judgedItems.length === coverageCount('none')
        ? 'none'
        : judgedItems.length === coverageCount('complete')
            ? 'complete'
            : coverageCount('partial') > 0
                ? 'partial'
                : coverageCount('unknown') > 0
                    ? 'unknown'
                    : 'partial';
    const complete = actualCostCoverage === 'complete';
    const actualBuilderCostExGst = complete
        ? roundCents(items
            .filter((item, index) => receipts[index] || judgedItems.includes(item))
            .reduce((sum, item) => sum + (item.actualBuilderCostExGst || 0), 0))
        : null;
    const actualMarginAmount = actualBuilderCostExGst == null
        ? null
        : roundCents(quoteRevenueExGst - actualBuilderCostExGst);
    const actualMarginPercent = actualBuilderCostExGst == null || quoteRevenueExGst <= 0
        ? null
        : Math.round((actualMarginAmount! / quoteRevenueExGst) * 10_000) / 100;

    return {
        items,
        quoteRevenueExGst,
        rateBookChargeOutIncGst,
        rateBookChargeOutExGst,
        unmatchedCostBaseExGst,
        pricingFloorExGst,
        pricingHeadroom,
        shortfall,
        underpricedItemCount,
        materialUnderpricedItemCount,
        actualBuilderCostExGst,
        actualMarginAmount,
        actualMarginPercent,
        actualCostCoverage,
    };
}

const EXPLICIT_MANUAL_AMOUNT_REASON = /^(?:stage5-manual|builder-manual|operator-manual|assistant-explicit)-/;
const BUILDER_APPROVED_AMOUNT_REASON = /^builder[\s_-]*approved.*(?:amount|price|package)/;

/**
 * A broad row edit is not evidence that the builder accepted its price.
 *
 * Stage 4 deliberately stamps title/description edits with `manuallyEdited`,
 * `humanEditedAt` and `editedBy`. Generated rows can also carry
 * `amountLocked` as a transport flag. Combining those broad fields would let
 * a copy-only edit freeze a stale, loss-making generated amount. Price
 * authority therefore needs a price-specific receipt: an explicit amount-lock
 * reason, amount/price edit timestamp, or amount/price editor.
 */
export function hasReviewableManualAmountAuthority(item: UnknownRecord): boolean {
    const locked = item?.amountLocked === true || item?.amount_locked === true;
    if (!locked) return false;

    const lockReason = String(item?.amountLockReason || item?.amount_lock_reason || '').trim().toLowerCase();
    const explicitManualReason = EXPLICIT_MANUAL_AMOUNT_REASON.test(lockReason)
        || BUILDER_APPROVED_AMOUNT_REASON.test(lockReason);
    const priceSpecificTimestamp = Boolean(
        item?.amountEditedAt
        || item?.amount_edited_at
        || item?.priceEditedAt
        || item?.price_edited_at
        || item?.amountLockedAt
        || item?.amount_locked_at,
    );
    const priceSpecificEditor = String(
        item?.amountEditedBy
        || item?.amount_edited_by
        || item?.priceEditedBy
        || item?.price_edited_by
        || '',
    ).trim().toLowerCase();
    const reviewablePriceEditor = ['user', 'user-edit', 'manual', 'operator', 'assistant', 'assistant-edit']
        .includes(priceSpecificEditor);

    return explicitManualReason || priceSpecificTimestamp || reviewablePriceEditor;
}

/**
 * Cost-ledger authority is deliberately separate from amount authority.
 *
 * A builder can edit the Stage 4 price-working lines without pinning a manual
 * lump-sum amount. The producer stamps that ledger `manual_edit`; activity
 * regeneration may refresh copy and method, but must not replace those exact
 * quantities/rates before the shared price-authority gate sees them.
 */
export function hasReviewableManualCostAuthority(item: UnknownRecord): boolean {
    const breakdown = breakdownFor(item);
    const costSource = String(breakdown?.costSource || breakdown?.cost_source || '').trim().toLowerCase();
    return item?.costBreakdownLocked === true
        || item?.cost_breakdown_locked === true
        || Boolean(
            item?.pricingEditedAt
            || item?.pricing_edited_at
            || item?.costBreakdownEditedAt
            || item?.cost_breakdown_edited_at
        )
        || costSource === 'manual_edit'
        || costSource === 'human_edited';
}

export function hasReviewableManualPriceAuthority(item: UnknownRecord): boolean {
    return hasReviewableManualAmountAuthority(item) || hasReviewableManualCostAuthority(item);
}

function roundUpClientIncGst(value: number, step = 10): number {
    if (!(value > 0)) return 0;
    const safeStep = Number.isFinite(step) && step > 0 ? step : 10;
    // Subtract a sub-cent epsilon so an exact $10 boundary is not promoted by
    // floating-point multiplication (for example 27,645.45 * 1.1).
    return roundCents(Math.ceil((value - 0.001) / safeStep) * safeStep);
}

/**
 * Enforce the deterministic Stage 4 floor at the point a generated price is
 * committed. An unresolved catalogue line remains unresolved and visible for
 * builder review; it may not, however, leave the generated client amount below
 * the authoritative cost/rate-book dollars that are already known.
 *
 * Explicit, reviewable builder/operator price locks are preserved. Copy edits
 * and generated `amountLocked` carrier rows are not manual price authority.
 */
export function reconcileGeneratedAmountsToPricingFloor(
    scope: unknown = [],
    isGstInclusive = true,
): UnknownRecord[] {
    if (!Array.isArray(scope)) return [];
    const scopeItems = scope.filter(Boolean) as UnknownRecord[];
    const displayIncludesGst = isGstInclusive !== false;
    const ledger = buildStage4PricingLedger(scopeItems, displayIncludesGst);

    return scopeItems.map((item, index) => {
        if (item?.amountAuthority !== 'committed') return item;
        if (hasReviewableManualPriceAuthority(item)) return item;
        const row = ledger.items[index];
        if (!row || !row.belowFloor || !(row.pricingFloorExGst > 0)) return item;

        const gstExempt = item?.gstExempt === true || item?.gst_exempt === true;
        const gstFactor = gstExempt ? 1 : 1.1;
        const safeIncGst = roundUpClientIncGst(row.pricingFloorExGst * gstFactor);
        const safeExGst = roundCents(gstExempt ? safeIncGst : safeIncGst / 1.1);
        const displayAmount = displayIncludesGst ? safeIncGst : safeExGst;
        const previousAmount = itemAmount(item);

        return {
            ...item,
            amount: displayAmount.toFixed(2),
            quoted_amount_inc_gst: safeIncGst,
            quoted_amount_ex_gst: safeExGst,
            pricingFloorAutoAdjusted: true,
            pricingFloorPreviousAmount: previousAmount,
        };
    });
}
